import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listRosterEvents,
  listTeamEvents,
} from '@sportninja/common/services/event'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { SnEvent } from '../types'

interface UseScheduleEventsArgs {
  teamId?: string
  /**
   * The team's roster for the selected season/division, read from
   * `/teams/{id}/schedules/V2`'s `team_roster_id` (see
   * `resolveRosterIdFromFilters`). Absent means no season is selected — "All
   * Seasons" — which switches to the team-wide endpoint.
   */
  rosterId?: string
  /**
   * Whether a season/division is selected at all. Distinguishes "All Seasons"
   * (team-wide list) from "a season is selected but its roster has not resolved
   * yet", which must stay idle rather than silently listing every season.
   */
  hasScheduleSelection?: boolean
  /** True while the schedule filters are still loading. */
  isResolvingRoster?: boolean
}

interface UseScheduleEventsReturn {
  events: SnEvent[]
  isLoading: boolean
  isError: boolean
  /**
   * Refetches the active list, honouring `staleTime` — a call inside the
   * 15s window is a no-op rather than a request. Safe to call on every user
   * gesture; the schedule calls it on a Past / Today / Upcoming switch.
   */
  refetch: () => void
}

/**
 * Events change often enough that a long window shows stale rows after a
 * teammate adds one, so lists go stale quickly and refetch on next focus.
 *
 * Paired with `refetchOnWindowFocus` below: this is what stops that refetch
 * from firing on every incidental click back into the tab.
 */
const EVENT_LIST_STALE_TIME = 1000 * 15

const EMPTY: SnEvent[] = []

/**
 * Events for the schedule list, from whichever endpoint fits the selection:
 *
 * - a season/division is selected → that roster's events
 * - "All Seasons"                 → every event the team has, across seasons
 *
 * The roster id is passed IN rather than fetched: `/teams/{id}/schedules/V2`
 * (already loaded for the filter dropdowns) now returns `team_roster_id`, so
 * the former `/teams/{id}/rosters` round-trip is gone.
 *
 * When a season is selected but no roster resolves, this stays idle and
 * returns nothing. Falling back to the team-wide list there would show other
 * seasons' events under a specific season's filter.
 */
export const useScheduleEvents = ({
  teamId,
  rosterId,
  hasScheduleSelection = false,
  isResolvingRoster = false,
}: UseScheduleEventsArgs): UseScheduleEventsReturn => {
  'use memo'

  const queryClient = useQueryClient()

  const isAllSeasons = Boolean(teamId) && !hasScheduleSelection
  const isRosterScoped = Boolean(teamId) && hasScheduleSelection

  const rosterQuery = useQuery({
    queryKey: queryKeys.events.byRoster(teamId, rosterId),
    queryFn: () => listRosterEvents(teamId as string, rosterId as string),
    enabled: isRosterScoped && Boolean(rosterId),
    staleTime: EVENT_LIST_STALE_TIME,
    // Overrides the app-wide `refetchOnWindowFocus: false`, for these two
    // queries only. An event edited on another device (phone, second tab)
    // otherwise stays stale on this one indefinitely: the schedule's Past /
    // Today / Upcoming tabs re-filter the SAME fetched list client-side rather
    // than re-querying, so switching period never refreshes it, and the events
    // endpoints take no date params to key a per-period fetch on anyway.
    // Returning to the tab is the moment the user expects to see the change.
    refetchOnWindowFocus: true,
  })

  const teamQuery = useQuery({
    queryKey: queryKeys.events.byTeam(teamId),
    queryFn: () => listTeamEvents(teamId as string),
    enabled: isAllSeasons,
    staleTime: EVENT_LIST_STALE_TIME,
    // See the roster query above.
    refetchOnWindowFocus: true,
  })

  const active = isAllSeasons ? teamQuery : rosterQuery

  // A season whose roster has settled to "none" is done, not loading —
  // otherwise the list would wait forever on a query that will never run.
  const waitingOnRoster = isRosterScoped && isResolvingRoster

  // NOT `query.refetch()` and NOT `invalidateQueries` — both bypass staleTime
  // and hit the network on every call, so flipping between the period tabs
  // would fire a request per click. `refetchQueries({ stale: true })` refetches
  // only when the 15s window has actually lapsed, which is the intent: catch an
  // edit made elsewhere, without re-requesting an unchanged list.
  const refetch = () => {
    if (!isAllSeasons && !isRosterScoped) {
      return
    }
    queryClient.refetchQueries({
      queryKey: isAllSeasons
        ? queryKeys.events.byTeam(teamId)
        : queryKeys.events.byRoster(teamId, rosterId),
      type: 'active',
      stale: true,
    })
  }

  return {
    events: active.data ?? EMPTY,
    isLoading: waitingOnRoster || active.isLoading,
    isError: active.isError,
    refetch,
  }
}
